import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LineChart } from '@mui/x-charts/LineChart'
import formatDate from 'date-fns/format'
import { LineSeriesType } from '@mui/x-charts'
import { svSE } from '@mui/x-data-grid/locales'
import { useTranslation } from 'react-i18next'
import RadioGroupLabeled from '../RadioGroupLabeled'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { GridColDef } from '@mui/x-data-grid-pro'
import Grid from '@mui/material/Grid'
import { Box, Typography } from '@mui/material'
import { IssueEntity } from 'types/IssueEntity'
import { formatSeries, getDataForChart, groupByTimeUnit } from './utils'
import { formatDateYYYYMMDD, formatPercentage } from '../../../packages/utils/numberFormatters'
import { useLogUserEvent } from '../../../hooks/useLogUserEvent'
import UserEventApi from '../../../api/UserEventApi'
import RoutedDropdownSelect from '../../components/RoutedDropdownSelect'
import { useQuery } from '../../../hooks/useQuery'
import startOfQuarter from 'date-fns/startOfQuarter'
import { startOfYear } from 'date-fns'

const dropdownItems = [
    {
        id: 'commitment',
        label: 'Säkerställandegrad',
    },
    {
        id: 'subscription',
        label: 'Utfall',
    },
    {
        id: 'guarantor-fee-issue',
        label: 'Garantiersättning',
    },
    {
        id: 'discount',
        label: 'Rabatt',
    },
] as const

type TrendGraphType = (typeof dropdownItems)[number]['id']

const TrendChart = ({ isLoading, issues }: { isLoading: boolean; issues: IssueEntity[] }) => {
    const [selectedTimeUnit, setSelectedTimeUnit] = useState<'quarter' | 'year'>('quarter')
    const {
        i18n: { language: locale },
    } = useTranslation()
    const query = useQuery()
    const navigate = useNavigate()

    const handleChangeSerie = ([id]: string[]) => {
        query.set('serie', id)
        navigate('?' + query.toString())
    }
    const defaultGraph = 'commitment'
    const chartRoutingParam = 'graph'
    const selectedChart: TrendGraphType =
        dropdownItems.find((i) => i.id === query.get(chartRoutingParam))?.id || defaultGraph
    const {
        excludeNullValues,
        seriesData: dataset,
        series,
    } = React.useMemo(() => getDataForChart(selectedChart, issues), [selectedChart, issues])

    const defaultSerie = series[0].id
    const serieRoutingParam = 'serie'
    const selectedSerie = series.find((s) => s.id === query.get(serieRoutingParam))?.id || defaultSerie

    useLogUserEvent(UserEventApi.EVENT_TYPES.STATS_TREND_GRAPHS_SELECTION, {
        selectedChart,
        selectedSerie,
        selectedTimeUnit,
    })

    const groupedDataset = groupByTimeUnit(
        dataset,
        selectedTimeUnit,
        series.map((serie) => serie.id),
        excludeNullValues,
    )
    const formattedDataset = formatSeries(groupedDataset, selectedTimeUnit)

    const columns: GridColDef[] = [
        {
            field: 'time',
            headerAlign: 'left',
            headerName: '',
        },
        {
            field: `${selectedSerie}Count`,
            headerAlign: 'left',
            headerName: 'Antal',
        },
        {
            field: `${selectedSerie}Min`,
            headerAlign: 'left',
            headerName: 'Min',
            type: 'number',
            valueGetter: (val: number | null = null) =>
                val === null ? '-' : `${formatPercentage(val, { decimals: 1, locale })}`,
        },
        {
            field: `${selectedSerie}Max`,
            headerAlign: 'left',
            headerName: 'Max',
            type: 'number',
            valueGetter: (val: number | null = null) =>
                val === null ? '-' : `${formatPercentage(val, { decimals: 1, locale })}`,
        },
        {
            field: `${selectedSerie}Average`,
            headerAlign: 'left',
            headerName: 'Medel',
            valueGetter: (val: number | null = null) =>
                val === null ? '-' : `${formatPercentage(val, { decimals: 1, locale })}`,
        },
    ]

    const formattedSeries: Omit<LineSeriesType, 'type'>[] = series.map((serie) => ({
        ...serie,
        curve: 'catmullRom',
        highlightScope: {
            highlight: 'series',
            fade: 'global',
        },
        valueFormatter: (value: number | null) => `${value == null ? '-' : formatPercentage(value, { locale })}`,
    }))

    return (
        <Grid container spacing={10}>
            <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ marginBottom: 4 }}>
                    <RoutedDropdownSelect
                        defaultItem={defaultGraph}
                        items={dropdownItems}
                        label='Välj graf'
                        routingParam={chartRoutingParam}
                    />
                </Box>
                <RadioGroupLabeled<'quarter' | 'year'>
                    label='Tidsenhet'
                    onChange={setSelectedTimeUnit}
                    options={{ quarter: 'Kvartal', year: 'År' }}
                    value={selectedTimeUnit}
                />
                <LineChart
                    dataset={formattedDataset}
                    height={500}
                    highlightedItem={{
                        seriesId: selectedSerie,
                    }}
                    loading={isLoading}
                    margin={{
                        top: 100,
                        right: 30,
                    }}
                    xAxis={[
                        {
                            dataKey: 'date',
                            tickInterval: (d) =>
                                selectedTimeUnit === 'quarter'
                                    ? formatDateYYYYMMDD(d) === formatDateYYYYMMDD(startOfQuarter(d))
                                    : formatDateYYYYMMDD(d) === formatDateYYYYMMDD(startOfYear(d)),

                            scaleType: 'point',
                            valueFormatter: (value) =>
                                formatDate(value, selectedTimeUnit === 'quarter' ? 'yyyy QQQ' : 'yyyy'),
                        },
                    ]}
                    onMarkClick={(_event, { seriesId }) => handleChangeSerie([seriesId as string])}
                    onLineClick={(_event, { seriesId }) => handleChangeSerie([seriesId as string])}
                    series={formattedSeries}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant='h6' gutterBottom marginBottom={4}>
                    <RoutedDropdownSelect
                        defaultItem={defaultSerie}
                        items={series}
                        label='Välj serie'
                        routingParam={serieRoutingParam}
                    />
                </Typography>
                <DataGridPro
                    columns={columns}
                    loading={isLoading}
                    localeText={svSE.components.MuiDataGrid.defaultProps.localeText}
                    rows={formattedDataset}
                    hideFooter
                    sx={{ height: 'auto' }}
                    showToolbar
                    disableColumnFilter
                    disableColumnSelector
                    slotProps={{ toolbar: { showQuickFilter: false } }}
                />
            </Grid>
        </Grid>
    )
}

export default TrendChart
